// The conversation as the app shows it, built from the history pages and the stream's events
// (GET /api/conversation and GET /api/stream). Pure, so it is tested without a browser.
import type { AttachmentRef } from "./attachments.js";
import { clockTime } from "./times.js";

export interface Item {
  seq: number;
  /** The turn this item belongs to. */
  turnId: string;
  kind: "user" | "assistant" | "tool";
  /** When it was written, in epoch ms; 0 or missing for an item stored before items had one. */
  createdAt?: number;
  text?: string;
  /** A user item: the files attached to it, in the person's AlphaLab Drive. */
  attachments?: AttachmentRef[];
  /** A tool call: its name, status, and the app's projection of its arguments and result. */
  tool?: string;
  status?: "running" | "completed" | "failed";
  args?: string;
  result?: string;
  /** AlphaLab links found in the result. */
  links?: string[];
  /** A chain's own message to its next turn: not the person's, so not shown. */
  continuation?: boolean;
}

export type TurnState = "recorded" | "running" | "completed" | "failed" | "cancelled";

export interface Turn {
  turnId: string;
  state: TurnState;
  endReason?: string;
  /** `agenda` for a scheduled check-in and its chain, `continue` for the person's chain (unattended.md section 7). */
  origin?: "agenda" | "continue" | "tasks";
  what?: string;
  /** Why a failed turn failed. */
  error?: string;
  /** A chain's turn: the turn it continues. */
  continuationOf?: string;
}

/**
 * The person's message from Send until its own user item is shown (design-revamp.md section 6):
 * `sending` while the request is out (and repeated with the same key after a lost answer),
 * `accepted` once a `202` named its turn, `not_sent` after a definite refusal, and `unconfirmed`
 * when no definite answer came within 2 minutes: Retry only, the key kept.
 */
export interface Pending {
  key: string;
  text: string;
  /** The files it carries: a Retry sends the same body with the same key. */
  attachments?: AttachmentRef[];
  state: "sending" | "accepted" | "not_sent" | "unconfirmed";
  /** When the person sent it, in epoch ms; a Retry keeps it. */
  sentAt?: number;
  /** The turn a `202` recorded; its user item replaces this message (P2). */
  turnId?: string;
  /** Why it was not sent, for the person. */
  reason?: string;
}

export interface Chat {
  /** By seq, oldest first, each seq once. */
  items: Item[];
  /** The seq before which older history exists, if any. */
  before?: number;
  /** The active turn, or the last one this browser saw end. */
  turn?: Turn;
  /** Turns this browser saw end, newest last, so a late answer cannot revive one. */
  ended: string[];
  /** Live text of the running turn's step in flight, until its items arrive. */
  live?: { turnId: string; text: string };
  /** The stream asked for the conversation to be reloaded. */
  reload: boolean;
  /** The first history page has arrived (or the conversation was cleared): an empty one is really empty. */
  loaded: boolean;
  /** One message of the person's not yet shown as its item (P4: one at a time). */
  pending?: Pending;
}

export const emptyChat: Chat = { items: [], ended: [], reload: false, loaded: false };

export type ChatEvent =
  | { type: "history"; items: Item[]; before?: number; replace: boolean }
  | { type: "item"; item: Item }
  /** The running turn's text so far; with `replace`, it replaces what was shown. */
  | { type: "delta"; turnId: string; text: string; replace?: boolean }
  | { type: "turn"; turn: Turn }
  | { type: "reset" }
  /** Reset conversation: what was shown is no longer the conversation. */
  | { type: "cleared" }
  /** Send or Retry: the message shows at once (P1), `at` the time Send was pressed. */
  | { type: "pending"; key: string; text: string; attachments?: AttachmentRef[]; at?: number }
  | { type: "pending_accepted"; key: string; turnId: string }
  | { type: "pending_failed"; key: string; state: "not_sent" | "unconfirmed"; reason: string }
  | { type: "pending_discard"; key: string };

export const TERMINAL: readonly TurnState[] = ["completed", "failed", "cancelled"];
/** How many ended turns are remembered. */
const ENDED_KEPT = 50;

export function isRunning(turn: Turn | undefined): boolean {
  return turn !== undefined && !TERMINAL.includes(turn.state);
}

export function lastSeq(chat: Chat): number {
  return chat.items.at(-1)?.seq ?? 0;
}

/** The pending message once its own user item is among `items` (P2): matched by turn, never by text. */
function settlePending(pending: Pending | undefined, items: Item[]): Pending | undefined {
  if (!pending?.turnId) return pending;
  return items.some((i) => i.kind === "user" && i.turnId === pending.turnId) ? undefined : pending;
}

function merge(items: Item[], more: Item[]): Item[] {
  const bySeq = new Map(items.map((i) => [i.seq, i]));
  for (const item of more) bySeq.set(item.seq, item);
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

export function reduce(chat: Chat, event: ChatEvent): Chat {
  switch (event.type) {
    case "history": {
      // A fresh first page replaces what is shown, except items the stream delivered after it.
      const newest = event.items.reduce((max, i) => Math.max(max, i.seq), 0);
      const items = event.replace ? merge(chat.items.filter((i) => i.seq > newest), event.items) : merge(chat.items, event.items);
      const next = { ...chat, items, before: event.before, pending: settlePending(chat.pending, items) };
      return event.replace ? { ...next, reload: false, loaded: true } : next;
    }
    case "item": {
      // A step's own items replace the live text that streamed it.
      const items = merge(chat.items, [event.item]);
      const stored = event.item.kind !== "user" && chat.live?.turnId === event.item.turnId;
      return { ...chat, items, live: stored ? undefined : chat.live, pending: settlePending(chat.pending, [event.item]) };
    }
    case "delta": {
      const text = event.replace || chat.live?.turnId !== event.turnId ? event.text : chat.live.text + event.text;
      return { ...chat, live: { turnId: event.turnId, text } };
    }
    case "turn":
      return turnUpdate(chat, event.turn);
    case "reset":
      return { ...chat, reload: true };
    case "cleared":
      return { ...emptyChat, loaded: true };
    case "pending": {
      // A new message replaces one not sent; the same key (Retry) keeps its place. One on its way,
      // or unconfirmed, may have been recorded, so it is never replaced by another (P3).
      const held = chat.pending && chat.pending.key !== event.key && (chat.pending.state === "sending" || chat.pending.state === "unconfirmed");
      if (held) return chat;
      const sentAt = chat.pending?.key === event.key ? chat.pending.sentAt : event.at;
      const attachments = event.attachments?.length ? { attachments: event.attachments } : {};
      return { ...chat, pending: { key: event.key, text: event.text, ...attachments, state: "sending", ...(sentAt !== undefined ? { sentAt } : {}) } };
    }
    case "pending_accepted": {
      if (chat.pending?.key !== event.key) return chat;
      const pending: Pending = { ...chat.pending, state: "accepted", turnId: event.turnId, reason: undefined };
      return { ...chat, pending: settlePending(pending, chat.items) };
    }
    case "pending_failed":
      return chat.pending?.key === event.key ? { ...chat, pending: { ...chat.pending, state: event.state, reason: event.reason } } : chat;
    case "pending_discard":
      // Only a message definitely not sent can be discarded; an unconfirmed one keeps its key.
      return chat.pending?.key === event.key && chat.pending.state === "not_sent" ? { ...chat, pending: undefined } : chat;
  }
}

/** Whether the composer must wait: a message is on its way (P4), or a turn runs. */
export function composerBusy(chat: Chat): boolean {
  return chat.pending?.state === "sending" || chat.pending?.state === "unconfirmed" || isRunning(chat.turn);
}

function turnUpdate(chat: Chat, turn: Turn): Chat {
  const terminal = TERMINAL.includes(turn.state);
  // A late answer to a send or cancel never revives a turn already seen ending.
  if (!terminal && chat.ended.includes(turn.turnId)) return chat;
  // Nor shows an older turn as running in place of a newer one.
  if (!terminal) return chat.turn && turn.turnId < chat.turn.turnId ? chat : { ...chat, turn };

  const ended = chat.ended.includes(turn.turnId) ? chat.ended : [...chat.ended, turn.turnId].slice(-ENDED_KEPT);
  // An older turn's end never replaces a newer turn, running or not. Turn IDs are ULIDs, so they
  // sort by when the turn was recorded.
  const current = chat.turn && turn.turnId < chat.turn.turnId ? chat.turn : turn;
  const live = chat.live?.turnId === turn.turnId ? undefined : chat.live;
  return { ...chat, turn: current, ended, live };
}

/** The conversation in order, without the messages a chain records for itself. */
export function timeline(chat: Chat): Item[] {
  return chat.items.filter((i) => !i.continuation);
}

/** Ends for AlphaLab Credits, whose assistant item already tells the person what happened. */
const CREDITS_ENDS = new Set(["capacity_exhausted", "no_plan", "billing_unavailable"]);

/** What to tell the person about how a turn ended, or nothing when its items say it. */
export function endNotice(turn: Turn | undefined): string | undefined {
  if (!turn || !TERMINAL.includes(turn.state) || turn.state === "completed") return undefined;
  if (turn.state === "cancelled") return turn.endReason === "yielded" ? "Scheduled work paused for you." : "Stopped.";
  if (turn.endReason === "no_answer") return "Quanty One stopped without answering.";
  if (turn.endReason === "reconnect_required") return "Reconnect AlphaLab so Quanty One can use its tools.";
  // Quanty One's own message says why (credits.md section 4).
  if (turn.endReason !== undefined && CREDITS_ENDS.has(turn.endReason)) return undefined;
  return turn.error ? `The agent could not finish this turn: ${turn.error}` : "The agent could not finish this turn.";
}

/** The marker lines a scheduled check-in's message (unattended.md section 7) and a task turn's (tasks.md section 10) start with. */
const CHECK_IN = /^Scheduled check-in #\S+ at (\S+)\.\nRequest: ([^\n]*)/;
const TASK_TURN = /^Task turn #\S+ at (\S+)\.\nRequest: ([^\n]*)/;

/**
 * A scheduled check-in's or a task turn's message, as its kind, request and time (`at` to show, `time`
 * in epoch ms when the marker's time reads as one); undefined for the person's own.
 */
export function checkIn(item: Item): { what: string; at: string; time?: number; kind: "scheduled" | "task" } | undefined {
  if (item.kind !== "user" || !item.text) return undefined;
  const task = TASK_TURN.exec(item.text);
  const match = task ?? CHECK_IN.exec(item.text);
  if (!match) return undefined;
  const kind = task ? "task" : "scheduled";
  const time = new Date(match[1]).getTime();
  return Number.isNaN(time) ? { what: match[2], at: match[1], kind } : { what: match[2], at: clockTime(time), time, kind };
}

/** What the status line says while a turn runs. */
export function workingNotice(turn: Turn | undefined): string | undefined {
  if (!isRunning(turn)) return undefined;
  if (turn!.origin === "tasks") return `Quanty One is working on your tasks${turn!.what ? `: ${turn!.what}` : ""}`;
  return turn!.origin === "agenda" ? `Quanty One is doing scheduled work${turn!.what ? `: ${turn!.what}` : ""}` : "Quanty One is working…";
}
